import { useEffect, useState } from 'react';
import { api } from '../api';

// Per-stop activities picker — used once per stop on multi-city trips.
// onToggle(activity) flips an activity in or out of the picks for THIS stop.
// Picks come in as the picked[] array so we can highlight what's already on.
export default function StopActivitiesPicker({
  destination,
  picked = [],
  onToggle,
  scaleForTravelers,
  travelersLabel,
  collapsedByDefault = false,
}) {
  const [activities, setActivities] = useState(null);
  const [open, setOpen] = useState(!collapsedByDefault);

  useEffect(() => {
    if (!destination?.id || !open) return;
    api.getActivities(destination.id)
      .then(all => {
        // For bundled sub-cities, filter activities to those whose description
        // or tip mentions the sub-city name. Falls back to all if no match.
        const subName = destination._bundleSubName || (
          (destination.city || '').includes(' + ')
            ? destination.city.split(' + ')[0].trim()
            : null
        );
        if (subName) {
          const re = new RegExp(`\\b${subName.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i');
          const filtered = (all || []).filter(a =>
            re.test(a.name || '') || re.test(a.description || '') || re.test(a.tip || '') || re.test(a.booking_tip || '')
          );
          setActivities(filtered.length > 0 ? filtered : all || []);
        } else {
          setActivities(all || []);
        }
      })
      .catch(() => setActivities([]));
  }, [destination?.id, destination?._bundleSubName, destination?.city, open]);

  const isPicked = (id) => picked.some(p => p.id === id);
  const pickedCount = picked.length;
  const pickedTotal = picked.reduce((s, a) => s + (a.price_for_two || 0), 0);

  return (
    <div className="bg-navy-light rounded-2xl border border-brand-ink/10 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full p-3.5 flex items-center justify-between gap-2 text-left hover:bg-brand-ink/5 transition"
      >
        <div className="min-w-0 flex items-center gap-2.5">
          <span className="font-bold text-brand-ink truncate">{destination.city}</span>
          <span className="text-xs text-brand-ink/55 shrink-0">
            {pickedCount > 0 ? `· ${pickedCount} picked · $${scaleForTravelers ? scaleForTravelers(pickedTotal) : pickedTotal}` : '· No activities yet'}
          </span>
        </div>
        <span className="text-brand-ink/35 shrink-0">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 border-t border-brand-ink/10 pt-3 space-y-2">
          {activities === null && <div className="text-xs text-brand-ink/55">Loading…</div>}
          {activities && activities.length === 0 && <div className="text-xs text-brand-ink/55">No activities curated for {destination.city} yet.</div>}
          {activities && activities.map(a => {
            const sel = isPicked(a.id);
            const price = scaleForTravelers ? scaleForTravelers(a.price_for_two) : a.price_for_two;
            return (
              <button
                key={a.id}
                onClick={() => onToggle(a)}
                className={`w-full text-left rounded-xl border p-3 transition ${
                  sel ? 'border-gold ring-2 ring-gold/30 bg-gold/5' : 'border-brand-ink/10 hover:border-brand-ink/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-4 h-4 rounded border-2 text-[10px] ${sel ? 'bg-gold border-gold text-navy' : 'border-brand-ink/30'}`}>
                        {sel ? '✓' : ''}
                      </span>
                      <span>{a.name}</span>
                    </div>
                    <div className="text-[11px] text-brand-ink/65 mt-1 ml-6">{a.duration} · {a.category?.replace('_', ' ')}</div>
                    <div className="text-xs text-brand-ink/75 mt-1 ml-6">{a.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-gold">{a.price_for_two === 0 ? 'Free' : `$${price}`}</div>
                    <div className="text-[10px] text-brand-ink/55">for {travelersLabel || '2 travelers'}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
